import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

export interface AgendaSidebarPrestador {
  id: string;
  nome: string;
  avatarUrl?: string | null;
}

export interface AgendaSidebarProps {
  prestadores: AgendaSidebarPrestador[];
  selecionadoId: string | null;
  onSelecionar: (id: string | null) => void;
  isLoading: boolean;
}

function inicial(nome: string): string {
  return nome.trim().charAt(0).toUpperCase() || '?';
}

function rowClass(ativo: boolean): string {
  return cn(
    'flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left text-sm transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    ativo && 'bg-muted font-semibold',
  );
}

export function AgendaSidebar({
  prestadores,
  selecionadoId,
  onSelecionar,
  isLoading,
}: AgendaSidebarProps) {
  const { t } = useTranslation();

  return (
    <aside className="flex w-72 flex-shrink-0 flex-col overflow-hidden bg-card">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-soft">
        <h2 className="text-sm font-semibold">{t('agenda.sidebar.title')}</h2>
      </div>

      <div className="flex flex-col gap-1 p-3 overflow-y-auto flex-1">
        {isLoading ? (
          <>
            <Skeleton className="h-10 w-full rounded-lg" />
            <Skeleton className="h-10 w-full rounded-lg" />
            <Skeleton className="h-10 w-full rounded-lg" />
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => onSelecionar(null)}
              aria-pressed={selecionadoId === null}
              className={rowClass(selecionadoId === null)}
            >
              {t('agenda.sidebar.allProviders')}
            </button>

            {prestadores.length === 0 && (
              <p className="px-1 pt-2 text-sm text-muted-foreground">
                {t('agenda.sidebar.empty')}
              </p>
            )}

            {prestadores.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => onSelecionar(p.id)}
                aria-pressed={selecionadoId === p.id}
                className={rowClass(selecionadoId === p.id)}
              >
                <span
                  className="flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted text-xs font-medium"
                  aria-hidden
                >
                  {p.avatarUrl ? (
                    <img src={p.avatarUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    inicial(p.nome)
                  )}
                </span>
                <span className="truncate">{p.nome}</span>
              </button>
            ))}
          </>
        )}
      </div>
    </aside>
  );
}
